"use client"

import { Brand, HomePage, PriceRangeResponse } from "@/api/generated/BMWInterfaces"
import { useIsDesktop } from "@/lib/common/MediaQueries"
import { pageview } from "@/lib/gtm"
import styled from "@emotion/styled"
import { useCallback, useEffect, useRef, useState } from "react"
import Advantages from "./Advantages"
import CarInterest from "./CarInterest"
import FAQComp from "./FAQComp"
import Hero from "./Hero/Hero"
import HeroDrop from "./HeroDrop"
import MoreOffers from "./MoreOffers"
import PremiumDeals from "./PremiumDeals"
import SpecialBanner from "./SpecialBanner"
import StickyButton from "./StickyButton"

const OutLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`

const Layout = styled("section")<{ $hasMobilePadding: boolean }>`
  padding-bottom: 32px;
  /* ${(props) => props.$hasMobilePadding && "padding-top: 32px"}; */
  gap: 32px;
  align-items: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 64px;
    padding-top: 64px;
    gap: 64px;
  }
  display: flex;
  flex-direction: column;
`
const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 40px;
  }
`

// Offsets anchor scrolling (e.g. #car-interest hash links) so the fixed header
// (60px mobile / 80px desktop) doesn't cover the section on arrival.
const CarInterestSection = styled.section`
  scroll-margin-top: 84px;
  ${(props) => props.theme.breakpoints.up("md")} {
    scroll-margin-top: 104px;
  }
`

const InnerLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: hidden;
`
type Props = {
  data: HomePage
  brands: Brand[]
  priceRange: PriceRangeResponse
}

export default function Homepage({ data, brands, priceRange }: Props): React.ReactElement {
  const [showSticky, setShowSticky] = useState(false)
  const heroRef = useRef<HTMLDivElement>(null)
  const carInterestRef = useRef<HTMLDivElement>(null)
  const afterHeroRef = useRef<HTMLDivElement>(null)
  const { faq, mainBanner, topModels, specialSaleBanner } = data
  const { minPrice, maxPrice } = priceRange
  const isDesktop = useIsDesktop()
  const handleStickyButtonClick = useCallback(() => {
    if (afterHeroRef.current) {
      const yOffset = -80 // Adjust this value to control how much higher it scrolls
      const element = afterHeroRef.current
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset
      window.scrollTo({ top: y, behavior: "smooth" })
    }
  }, [])
  useEffect(() => {
    const handleScroll = (): void => {
      if (heroRef.current && isDesktop) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom
        setShowSticky(heroBottom <= 350)
      }
      if (heroRef.current && carInterestRef.current && !isDesktop) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom
        const carInterestTop = carInterestRef.current.getBoundingClientRect().top
        setShowSticky(heroBottom <= 137 && (carInterestTop > 510 || carInterestTop < -430))
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])
  useEffect(() => {
    pageview()
  }, [])
  const showSaleBanner =
    !!specialSaleBanner?.isActive &&
    !!specialSaleBanner?.duoDate &&
    new Date(specialSaleBanner.duoDate) > new Date()
  return (
    <OutLayout>
      {mainBanner && (
        <section ref={heroRef}>
          <Hero
            handleStickyButtonClick={handleStickyButtonClick}
            data={mainBanner?.heros?.mainBannerHero}
          />
        </section>
      )}
      <Layout $hasMobilePadding={showSaleBanner}>
        <InnerWrapper>
          {showSaleBanner && <SpecialBanner data={specialSaleBanner} />}
          {!!topModels && <PremiumDeals data={topModels} />}
        </InnerWrapper>
        <InnerLayout ref={afterHeroRef}>
          <CarInterestSection ref={carInterestRef} id="car-interest">
            <CarInterest data={brands} minPrice={minPrice} maxPrice={maxPrice} />
          </CarInterestSection>
          <MoreOffers />
          <Advantages />
          {mainBanner && mainBanner.media.videoLink && <HeroDrop data={mainBanner.media} />}
        </InnerLayout>

        <FAQComp data={faq} />
        {/* Last child of Layout so the bottom-sticky CTA floats above the fold and then
            settles here, keeping Layout's usual bottom spacing beneath it. */}
        {showSticky && <StickyButton />}
      </Layout>
    </OutLayout>
  )
}
